import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Wrench, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [loginField, setLoginField] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  // Trigger seed on mount (idempotent)
  useEffect(() => {
    api.post("/auth/init").catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!loginField || !password) {
      toast.error("Identifiants requis");
      return;
    }
    setSubmitting(true);
    const result = await login(loginField, password);
    setSubmitting(false);
    if (result.ok) {
      toast.success("Bienvenue " + (result.user?.nom || ""));
      navigate("/", { replace: true });
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-[#f0f9f4] via-white to-[#e8f5ef]" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-lg bg-[#52a675] flex items-center justify-center shadow-lg shadow-[#52a675]/20">
              <Wrench className="w-7 h-7 text-white" strokeWidth={2.5} />
            </div>
            <div className="text-left">
              <div className="font-heading font-bold text-2xl text-gray-900 leading-none">
                CarMaintenance
              </div>
              <div className="text-xs text-gray-500 uppercase tracking-widest mt-0.5">
                Meyrin · Garage & Carrosserie
              </div>
            </div>
          </div>
        </div>

        <Card className="p-6 sm:p-8 shadow-xl border-gray-200">
          <h1 className="font-heading text-2xl font-bold text-gray-900 mb-1">
            Connexion à l'application
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            Accédez à votre espace de travail
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="login" className="text-sm font-medium">Identifiant</Label>
              <Input
                id="login"
                value={loginField}
                onChange={(e) => setLoginField(e.target.value)}
                placeholder="admin / comptoir / atelier / compta"
                autoComplete="username"
                data-testid="login-input"
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-sm font-medium">Mot de passe</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  data-testid="password-input"
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  data-testid="toggle-password-visibility"
                >
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-[#52a675] hover:bg-[#3d8a5c] text-white font-semibold"
              disabled={submitting}
              data-testid="login-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Se connecter"}
            </Button>
          </form>

        </Card>

        <p className="text-center text-xs text-gray-400 mt-6">
          Déconnexion automatique après 15 min d'inactivité · v0.1 · Phase 1 CRM
        </p>
      </div>
    </div>
  );
}
